<template>
  <payment-cashback
    v-if="enableCashback"
    v-model="cashback"
    :totale-offerta-ivato="totaleOffertaIvato"
    :currency="currency"
  />

  <input
    v-if="!allowedMethods.length || companyState.updating"
    class="input-hidden"
    type="radio"
    required
    name="method"
    :checked="false"
    @click.prevent
  />

  <pf-empty-state v-if="companyState.updating">
    <pf-empty-state-header :title="$t('Caricamento metodi di pagamento...')" heading-level="h4">
      <template #icon>
        <pf-empty-state-icon>
          <pf-spinner />
        </pf-empty-state-icon>
      </template>
    </pf-empty-state-header>
  </pf-empty-state>

  <template v-else>
    <form-group :name="$t('Metodo di pagamento')">
      <pf-radio
        v-if="allowedMethods.includes('custom')"
        id="payCustom"
        :label="paymentCustomLabel"
        name="method"
        required
        value="custom"
        :checked="modelValue == 'custom'"
        @change="change"
      />

      <template v-else>
        <pf-radio
          v-if="allowedMethods.includes('paypal')"
          id="payPayPal"
          name="method"
          required
          value="paypal"
          :checked="modelValue == 'paypal'"
          @change="change"
        >
          <template #label>
            PayPal
            <paypal-icon />
          </template>
        </pf-radio>

        <pf-radio
          v-if="allowedMethods.includes('axerve-paypal')"
          id="payPayPal"
          name="method"
          required
          value="axerve-paypal"
          :checked="modelValue == 'axerve-paypal'"
          @change="change"
        >
          <template #label>
            PayPal
            <paypal-icon />
          </template>
        </pf-radio>

        <pf-radio
          v-if="allowedMethods.includes('axerve-cc')"
          id="payCc"
          name="method"
          required
          value="axerve-cc"
          :checked="modelValue == 'axerve-cc'"
          @change="change"
        >
          <template #label>
            {{ $t('Carta di credito') }}
            <cc-mastercard-icon />{{ ' ' }}<cc-visa-icon />{{ ' ' }}
          </template>
        </pf-radio>
      </template>
    </form-group>

    <form-group  v-if="askRecurring" :name="$t('Pagamento ricorrente')">
      <label>
        <input type="checkbox" name="enable_recurring_payments"> {{ $t('Autorizza i pagamenti ricorrenti automatici fino ad un massimo di 10.000 €/CHF') }}
        <form-help-tooltip :text="$t('Selezionando questa opzione e completando il pagamento, tutte le prossime scadenze di pagamento, fino ad un massimo di 10.000 €/CHF, verranno automaticamente saldate.')" />
      </label>
    </form-group>
    <pf-alert
      style="max-width: 600;"
      v-else-if="login.has_recurring_payments && invoice"
      variant="warning"
      inline
      :title="$t('NB: Ti ricordiamo che sono attivi i pagamenti automatici. Questa scadenza verrà saldata automaticamente dal sistema con il metodo da te scelto.')"
    />
  </template>
</template>

<script lang="ts" setup>
import { ref, type Ref, computed, watch } from 'vue';
import { useCompany } from '../utils';
import { useLoginStore } from '@/store/login';
import { $t } from '@/i18n';
import PaymentCashback from './payment-cashback.vue';
import FormHelpTooltip from '@/components/FormHelpTooltip.vue';
import PaypalIcon from '@vue-patternfly/icons/paypal-icon';
import CcVisaIcon from '@vue-patternfly/icons/cc-visa-icon';
import CcMastercardIcon from '@vue-patternfly/icons/cc-mastercard-icon';
import { ORDINI_TESTINGMODE, PAYMENTMETHOD } from '@common/consts';

export type Method = typeof PAYMENTMETHOD[number] | 'custom';

const props = withDefaults(defineProps<{
  modelValue: Method | null | undefined,
  invoice?: boolean,
  freePeriod?: number,
  testingMode?: typeof ORDINI_TESTINGMODE[number]|null,
  enableCashback?: boolean,
  totaleOffertaIvato: number,
  currency: 'CHF' | 'EUR',
}>(), {
  freePeriod: 0,
  testingMode: 'rinnova',
});

const emit = defineEmits<{
  (name: 'update:modelValue', value: Method): void;
}>();

const login = useLoginStore();
const { state : companyState, result: company } = useCompany();
const cashback = ref(0);

const allowedMethods: Ref<Method[]> = computed(() => !props.invoice ? ['custom'] : company.value.metodi_pagamento);
const askRecurring = computed(() => !login.has_recurring_payments && (props.modelValue === 'axerve-cc' || props.modelValue === 'axerve-paypal'));

const paymentCustomLabel = computed(() =>  {
  if (props.freePeriod) {
    return props.testingMode === 'rinnova' ?
      $t('GRATIS. Se non disdici prima ti arriverà fattura tra {0} giorni', [props.freePeriod]) :
      $t('GRATIS per {0} giorni. Se non ci chiederei il rinnovo i servizi verranno sospesi e cancellati tra {0} giorni', [props.freePeriod]);
  }

  if (!company.value.fatturazione_mensile) {
    return company.value.pagamento;
  }

  return `${$t(company.value.pagamento)} - ${$t('con fatturazione mensile')}`;
});

watch(
  () => allowedMethods.value.length,
  (v) => {
    if (v === 1 && allowedMethods.value[0]) {
      emit('update:modelValue', allowedMethods.value[0]);
    }
  },
  { immediate: true },
);


function change(e: Event) {
  if (e.target instanceof HTMLInputElement) {
    emit('update:modelValue', e.target.value as Method);
  }
};
</script>
